import { Component, inject, Inject, ChangeDetectionStrategy } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { Order } from '../../models/order.model';
import { OrderService } from 'src/app/shared/services/order.service';
import { OrderState } from "../../enums/order-state.enum";
import { WebProductService } from "../../services/web-product.service";
import { ShipmentService } from "../../services/shipment.service";
import { ShipmentStateEnum } from "../../enums/shipment-state.enum";
import { ExternalProductionService } from "../../services/external-production.service";
import { ExternalProductionState } from "../../enums/external-production-state.enum";
import { OrganizationService } from "../../services/organization.service";
import { SubcontractRequestService } from "../../services/subcontract-request.service";
import { SubcontractRequestState } from "../../enums/subcontract-request-state.enum";
import { PlanningService } from "../../services/planning.service";

@Component({
    selector: 'app-delete-order',
    templateUrl: './delete-order.component.html',
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class DeleteOrderComponent {
  public progress = false;
  public error = null;
  private orderService = inject(OrderService);
  private webProductService = inject(WebProductService);
  private shipmentService = inject(ShipmentService);
  private externalProductionService = inject(ExternalProductionService);
  private organizationService = inject(OrganizationService);
  private subcontractRequestService = inject(SubcontractRequestService);
  private planningService = inject(PlanningService);

  constructor(
    public dialogRef: MatDialogRef<DeleteOrderComponent>,
    @Inject(MAT_DIALOG_DATA) public order: Order) {
  }

  public cancel(): void {
    this.dialogRef.close(null);
  }

  async edit(): Promise<void> {
    this.startUpdate();
    const organizationId = this.organizationService.currentOrganization.id;
    if (this.order.webOrderId) {
      const webProductId = this.order.products[0].webProduct;
      await this.webProductService.update(webProductId, { state: OrderState.FINISHED })
    }
    const shipments = await this.shipmentService.getByOrderId(this.order.id);
    for (const shipment of shipments) {
      await this.shipmentService.update(shipment.id, { state: ShipmentStateEnum.CANCELLED,
        shipDate: new Date().toISOString().slice(0, 10), shipTime: new Date().toLocaleTimeString('fr-FR') });
    }
    const externalProductions = await this.externalProductionService.getByOrderId(organizationId, this.order.id);
    for (const externalProduction of externalProductions) {
      await this.externalProductionService.update(externalProduction.id, { state: ExternalProductionState.CANCELLED })
      const requests = await this.subcontractRequestService
        .getByExternalProduction(externalProduction.organizationId, externalProduction.id);
      for (const request of requests) {
        await this.subcontractRequestService.update(request.id, { state: SubcontractRequestState.CANCELED });
      }
    }
    await this.planningService.archiveOrderOperations(organizationId, this.order.id);
    await this.orderService.update(this.order.id, { state: OrderState.FINISHED });
    await this.orderService.createUpdateStateTask(this.order.organizationId, this.order.id, OrderState.FINISHED);
    this.dialogRef.close(true)
  }

  private startUpdate(): void {
    this.progress = true;
    this.error = null;
  }
}
